'use client'
import React, { useState } from 'react'
import Section from '@/app/components/sections/Section'
import Heading from '@/app/components/atoms/Heading'
import EmployeeCard from '@/app/components/molecules/EmployeeCard'
import { AnimatePresence, motion } from 'motion/react'
import { clean } from '~/app/utils/sanitizeString'



const EmployeesSection = ({ section }) => {
  return (
    <Section
      className="!gap-y-8"
      paddingTop={clean(section.design.padding.spacingTop)}
      paddingBottom={clean(section.design.padding.spacingBottom)}
      variant={clean(section.design.color.color)}
    >
      <div className="col-span-full">
        <Heading tag="h3" type="h3" spacing='none'>
          {section.heading}
        </Heading>
      </div>

      {/* Other section-specific components */}
      <EmployeesSection.Department section={section} />
      <EmployeesSection.All section={section} />
      <EmployeesSection.Manual section={section} />
    </Section>
  )
}

EmployeesSection.Department = Department
EmployeesSection.All = All
EmployeesSection.Manual = Manual

export default EmployeesSection

function Department({ section }) {
  return (
    <>
      {clean(section.view) === 'department' && (
        <>
          {section.department.map((department, index) => (
            <Section
              paddingX="none"
              paddingBottom="none"
              paddingTop="none"
              tag={'div'}
              key={index}
              className="col-span-full"
            >
              <div className="col-span-full">
                <Heading>{department.title}</Heading>
              </div>
              {department.employees.map((employee, index) => (
                <EmployeeCard key={index} employee={employee} />
              ))}
            </Section>
          ))}
        </>
      )}
    </>
  )
}

function Manual({ section }) {
  return (
    <>
      {clean(section.view) === 'manual' && (
        <>
          {section.employees.map((employee, index) => (
            <EmployeeCard key={index} employee={employee} />
          ))}
        </>
      )}
    </>
  )
}

function All({ section }) {
  const { employees = [], placementTitles = [] } = section
  const [selectedTitle, setSelectedTitle] = useState('All') // Default to 'All' showing all employees

  // Function to handle title click
  const handleTitleClick = (title) => {
    setSelectedTitle((prevTitle) => (prevTitle === title ? 'All' : title)) // Toggle between title and 'All'
  }

  return (
    <>
      {section.view === 'all' && (
        <>
          <div className="flex overflow-auto col-span-full">
            {/* Button to show all employees */}
            <button
              onClick={() => setSelectedTitle('All')}
              className={`text-nowrap rounded-none py-2.5 px-6 ${selectedTitle === 'All' ? 'bg-rød text-lys' : 'text-rødtekst bg-white'}`}
            >
              Alle afdelinger
            </button>

            {/* Map over each placement title */}
            {[...new Set(placementTitles)].map((title: string, index) => (
              <React.Fragment key={index}>
                {/* Clickable title */}
                <button
                  className={`text-nowrap rounded-none py-2.5 px-6 ${selectedTitle === title ? 'bg-rød text-lys' : 'text-rødtekst bg-white'}`}
                  onClick={() => handleTitleClick(title)}
                >
                  {title}
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Show all employees if 'All' is selected */}
          {selectedTitle === 'All' && (
            <AnimatePresence mode="popLayout">
              {employees.map((employee, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.1 * index,
                    ease: [0.76, 0, 0.24, 1],
                  }}
                  className="col-span-full sm:col-span-4 xl:col-span-6 group"
                >
                  <EmployeeCard employee={employee} />
                </motion.div>
              ))}
            </AnimatePresence>
          )}

          {/* Conditionally show employees if a specific title is selected */}
          {[...new Set(placementTitles)].map((title, index) => (
            <React.Fragment key={index}>
              {selectedTitle === title && (
                <AnimatePresence mode="popLayout">
                  {employees
                    .filter((employee) => employee.placement === title)
                    .map((employee, index) => (
                      <motion.div
                        key={index}
                        transition={{
                          duration: 0.5,
                          delay: 0.1 * index,
                          ease: [0.76, 0, 0.24, 1],
                        }}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="col-span-full sm:col-span-4 xl:col-span-6 group"
                      >
                        <EmployeeCard employee={employee} />
                      </motion.div>
                    ))}
                </AnimatePresence>
              )}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  )
}
